import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
}

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles: Particle[] = [];
    const count = Math.min(50, Math.floor(window.innerWidth / 30));
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 1,
        speedX: (Math.random() - 0.5) * 0.3,
        speedY: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.4 + 0.1,
      });
    }
    particlesRef.current = particles;

    const animate = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;

        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(74, 141, 255, ${p.opacity})`;
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(74, 141, 255, ${0.1 * (1 - dist / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      frameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(frameRef.current);
    };
  }, []);

  return (
    <section id="hero" className="relative min-h-[92vh] flex items-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://storage.readdy-site.link/project_files/d2cadc96-e8bf-4a0a-9bca-6276c5c079f1/f0a48c51-0a13-42b0-97c4-2c9b45c1a651_ChatGPT-Image-May-9-2026-09_42_44-AM.png?v=3c3a7f8cc6af0f5bcf0237ff23b3b1a1"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#07111F]/90 via-[#07111F]/80 to-[#07111F]/95" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#07111F]/60 via-transparent to-[#07111F]/60" />

      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-primary/8 rounded-full blur-[100px] animate-pulse" />
      <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-[#4A8DFF]/5 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: "1s" }} />

      {/* Particle canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-[1] pointer-events-none"
      />

      {/* Decorative corner lines */}
      <div className="absolute top-20 left-10 w-32 h-px bg-gradient-to-r from-primary/30 to-transparent" />
      <div className="absolute top-20 left-10 w-px h-32 bg-gradient-to-b from-primary/30 to-transparent" />
      <div className="absolute bottom-20 right-10 w-32 h-px bg-gradient-to-l from-primary/30 to-transparent" />
      <div className="absolute bottom-20 right-10 w-px h-32 bg-gradient-to-t from-primary/30 to-transparent" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pt-24 pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <img
              src="https://storage.readdy-site.link/project_files/d2cadc96-e8bf-4a0a-9bca-6276c5c079f1/b5027a06-2bfe-4b78-9c38-62694df68a09_VantageSight-Logo.png?v=630e4191644108a3bde8ca642e68fe7f"
              alt="VantageSight AI Advisory"
              className="h-36 md:h-48 lg:h-56 w-auto object-contain drop-shadow-2xl"
            />
          </div>
          <span className="inline-block font-sans text-[14px] font-medium text-primary tracking-wider uppercase mb-6">
            AI Advisory &amp; Consulting
          </span>
          <h1 className="font-serif text-[36px] md:text-[48px] lg:text-[56px] font-semibold text-text-headline leading-[1.15] mb-8">
            Practical AI Strategy, Governance, Workforce, and Adoption Advice for Organizations Ready to Use AI Responsibly
          </h1>
          <p className="font-sans text-[17px] md:text-[19px] text-text-muted leading-relaxed max-w-3xl mx-auto mb-10">
            VantageSight helps companies, federal agencies, state agencies, and enterprise teams create the strategy, governance, workforce readiness, and adoption plans needed to move AI forward with clarity, confidence, and control.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="font-sans text-[16px] font-medium text-white bg-primary hover:bg-primary-light px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block"
            >
              Schedule a 30-Minute Consultation
            </Link>
            <a
              href="#services"
              className="font-sans text-[16px] font-medium text-primary border-2 border-primary hover:bg-primary hover:text-white px-8 py-4 rounded-full transition-colors whitespace-nowrap"
            >
              Explore Advisory Services
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}